import { tileAt, type MapDef } from '../world/map';
import { T } from '../world/tiles';
import { colorAt3 } from './groundPixels3';
import { fbm, hash2, hex, mix, noise2, smoothstep, type RGB } from './noise';

/**
 * Ground materials for the homes, the night market and the travel destinations.
 * Returns null for unknown materials (the caller paints the void colour).
 */
const K = {
  terrazzoBase: hex('#d9d2c2'),
  terrazzoChips: ['#8a7a68', '#b5534a', '#3f6f62', '#f2eee4', '#5a5a62'].map(hex),
  marketA: hex('#5d5a58'),
  marketB: hex('#6f6b66'),
  puddle: hex('#3a4252'),
  drain: hex('#2a2a2e'),
};

const scale = (c: RGB, k: number): RGB => [c[0] * k, c[1] * k, c[2] * k];

/** 磨石子 terrazzo: speckled stone chips in cement, with brass divider strips. */
function terrazzo(x: number, y: number): RGB {
  let c = scale(K.terrazzoBase, 0.96 + noise2(x * 0.2, y * 0.2, 201) * 0.08);
  const cx = Math.floor(x * 1.6), cy = Math.floor(y * 1.6);
  const h = hash2(cx, cy, 202);
  if (h < 0.11) c = mix(c, K.terrazzoChips[Math.floor(hash2(cx, cy, 203) * K.terrazzoChips.length)], 0.55);
  // Divider strips every 2 tiles, the classic grid.
  const u = ((x % 32) + 32) % 32, v = ((y % 32) + 32) % 32;
  if (u < 0.5 || v < 0.5) c = hex('#b89a5a');
  // Polished sheen.
  c = mix(c, hex('#ffffff'), smoothstep(0.62, 0.8, fbm(x / 40, y / 18, 204)) * 0.12);
  return c;
}

/** Night-market street: worn concrete, drain grates along the kerb, rain puddles. */
function marketStreet(map: MapDef, x: number, y: number): RGB {
  let c = mix(K.marketA, K.marketB, fbm(x / 24, y / 24, 211));
  c = scale(c, 0.93 + noise2(x * 1.1, y * 1.1, 212) * 0.14);
  if (hash2(Math.floor(x * 2), Math.floor(y * 2), 213) < 0.04) c = scale(c, 1.25);
  // Expansion joints.
  if (((x % 48) + 48) % 48 < 0.6) c = scale(c, 0.75);
  // Drain grates where the street meets the stalls.
  const ty = Math.floor(y / T);
  const edge = tileAt(map, Math.floor(x / T), ty - 1) !== 'n' || tileAt(map, Math.floor(x / T), ty + 1) !== 'n';
  if (edge) {
    const fy = y - ty * T;
    const top = tileAt(map, Math.floor(x / T), ty - 1) !== 'n';
    const inBand = top ? fy < 3 : fy > T - 3;
    if (inBand) return (Math.floor(x) % 2 === 0) ? K.drain : scale(K.marketA, 0.8);
  }
  // Puddles reflecting the lanterns.
  const p = fbm(x / 36, y / 20, 214);
  if (p > 0.66) {
    c = mix(c, K.puddle, smoothstep(0.66, 0.7, p) * 0.8);
    const glow = fbm(x / 9, y / 5, 215);
    if (glow > 0.6) c = mix(c, hex('#ff9a5a'), (glow - 0.6) * 0.9);
  }
  return c;
}

/** Distance (px) to the nearest tile that isn't `self`, looking two tiles out. */
function edgeDist(map: MapDef, x: number, y: number, self: string): number {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T, fy = y - ty * T;
  const other = (dx: number, dy: number) => tileAt(map, tx + dx, ty + dy) !== self;
  let d = 40;
  if (other(-1, 0)) d = Math.min(d, fx);
  if (other(1, 0)) d = Math.min(d, T - fx);
  if (other(0, -1)) d = Math.min(d, fy);
  if (other(0, 1)) d = Math.min(d, T - fy);
  if (other(-2, 0)) d = Math.min(d, fx + T);
  if (other(2, 0)) d = Math.min(d, 2 * T - fx);
  if (other(0, -2)) d = Math.min(d, fy + T);
  if (other(0, 2)) d = Math.min(d, 2 * T - fy);
  if (other(-1, -1)) d = Math.min(d, Math.hypot(fx, fy));
  if (other(1, -1)) d = Math.min(d, Math.hypot(T - fx, fy));
  if (other(-1, 1)) d = Math.min(d, Math.hypot(fx, T - fy));
  if (other(1, 1)) d = Math.min(d, Math.hypot(T - fx, T - fy));
  return d;
}

/** Taipei plaza: big polished granite slabs. */
function granite(x: number, y: number): RGB {
  const s = 24;
  const cx = Math.floor(x / s), cy = Math.floor(y / s);
  const u = x - cx * s, v = y - cy * s;
  if (u < 0.5 || v < 0.5) return hex('#8a8580');
  let c = mix(hex('#bfb9b0'), hex('#d6d0c6'), hash2(cx, cy, 221));
  if (hash2(Math.floor(x * 1.5), Math.floor(y * 1.5), 222) < 0.18) c = scale(c, 0.86 + hash2(Math.floor(x * 3), Math.floor(y * 3), 223) * 0.3);
  c = mix(c, hex('#ffffff'), smoothstep(0.6, 0.78, fbm(x / 50, y / 30, 224)) * 0.14);
  return c;
}

/** Taipei sidewalk: red and grey bricks in a herringbone-ish running bond. */
function brickWalk(x: number, y: number): RGB {
  const row = Math.floor(y / 4);
  const off = (row % 2) * 4;
  const col = Math.floor((x + off) / 8);
  const u = (x + off) - col * 8, v = y - row * 4;
  if (u < 0.5 || v < 0.5) return hex('#6e5f55');
  const h = hash2(col, row, 231);
  let c = h < 0.18 ? hex('#9a9a96') : mix(hex('#a94e3a'), hex('#c46a4c'), hash2(col, row, 232));
  c = scale(c, 0.94 + noise2(x * 0.8, y * 0.8, 233) * 0.12);
  if (v < 1.2) c = scale(c, 1.06);
  return c;
}

/** Station / MRT floor: light speckled stone tiles. */
function stationFloor(x: number, y: number): RGB {
  const s = 16;
  const cx = Math.floor(x / s), cy = Math.floor(y / s);
  const u = x - cx * s, v = y - cy * s;
  if (u < 0.5 || v < 0.5) return hex('#a9aeb2');
  let c = mix(hex('#dfe3e5'), hex('#eceff0'), hash2(cx, cy, 241));
  if (hash2(Math.floor(x * 2), Math.floor(y * 2), 242) < 0.1) c = scale(c, 0.9);
  c = mix(c, hex('#ffffff'), smoothstep(0.65, 0.8, fbm(x / 40, y / 14, 243)) * 0.18);
  return c;
}

/** Yellow tactile paving with raised bumps. */
function tactile(x: number, y: number): RGB {
  const c = hex('#f2c230');
  const u = ((x % 3) + 3) % 3, v = ((y % 3) + 3) % 3;
  const d = Math.hypot(u - 1.5, v - 1.5);
  return d < 0.7 ? scale(c, 1.12) : d < 1 ? scale(c, 0.82) : c;
}

/** Railway: grey ballast, sleepers and two shiny rails. */
function rails(x: number, y: number): RGB {
  let c = mix(hex('#6e6a66'), hex('#8a857e'), hash2(Math.floor(x * 2), Math.floor(y * 2), 251));
  const fy = ((y % 16) + 16) % 16;
  if (((x % 8) + 8) % 8 < 3 && fy > 2 && fy < 14) c = hex('#5a4030');
  if (Math.abs(fy - 4) < 0.8 || Math.abs(fy - 12) < 0.8) c = mix(hex('#b8bcc0'), hex('#e8ecef'), fy < 8 ? 0.6 : 0.3);
  return c;
}

/** Museum: polished dark marble with soft reflections. */
function marbleFloor(x: number, y: number): RGB {
  const s = 32;
  const cx = Math.floor(x / s), cy = Math.floor(y / s);
  const u = x - cx * s, v = y - cy * s;
  if (u < 0.4 || v < 0.4) return hex('#3a2e26');
  const vein = Math.abs(Math.sin((x + y * 0.6) / 9 + fbm(x / 20, y / 20, 261) * 6));
  let c = mix(hex('#6a5444'), hex('#8a6e58'), hash2(cx, cy, 262));
  if (vein < 0.08) c = mix(c, hex('#d8c8b0'), 0.5);
  c = mix(c, hex('#ffffff'), smoothstep(0.62, 0.8, fbm(x / 60, y / 22, 263)) * 0.1);
  return c;
}

/** Taroko marble cliff top: pale grey stone with mossy cracks and white veins. */
function cliff(x: number, y: number): RGB {
  let c = mix(hex('#9a9890'), hex('#c8c4ba'), fbm(x / 22, y / 22, 271));
  const vein = Math.abs(Math.sin((x * 0.8 - y) / 7 + fbm(x / 14, y / 14, 272) * 5));
  if (vein < 0.07) c = mix(c, hex('#f4f2ec'), 0.7);
  const moss = fbm(x / 30, y / 30, 273);
  if (moss > 0.62) c = mix(c, hex('#5f8a4a'), smoothstep(0.62, 0.75, moss) * 0.7);
  return scale(c, 0.93 + noise2(x * 0.9, y * 0.9, 274) * 0.12);
}

/** Liwu river: milky turquoise from marble dust, white rapids near the rocks. */
function river(map: MapDef, x: number, y: number): RGB {
  const d = edgeDist(map, x, y, 'q') + (noise2(x * 0.2, y * 0.2, 281) - 0.5) * 2;
  if (d < 1.4) return hex('#e8f4f0');
  let c = mix(hex('#7fd6cf'), hex('#2aa6b0'), smoothstep(2, 22, d));
  const flow = fbm(x / 10, y / 34 - 0, 282);
  if (flow > 0.64) c = mix(c, hex('#e8fbf8'), (flow - 0.64) * 2.2);
  return c;
}

/** Qixingtan: rounded grey/white pebbles. */
function pebbles(x: number, y: number): RGB {
  const s = 3.2;
  const cx = Math.floor(x / s), cy = Math.floor(y / s);
  const px = (cx + 0.5 + (hash2(cx, cy, 291) - 0.5) * 0.5) * s, py = (cy + 0.5 + (hash2(cx, cy, 292) - 0.5) * 0.5) * s;
  const d = Math.hypot(x - px, (y - py) * 1.2) / (s * 0.55);
  const tone = hash2(cx, cy, 293);
  let c = tone < 0.15 ? hex('#4a4a4e') : tone > 0.8 ? hex('#efece6') : mix(hex('#8a8884'), hex('#bdb8ae'), tone);
  if (d > 1) c = scale(c, 0.62);
  else if (d < 0.5 && y < py) c = scale(c, 1.1);
  return c;
}

/** The Pacific: deep blue far out, turquoise near the pebbles, lines of foam. */
function sea(map: MapDef, x: number, y: number): RGB {
  const d = edgeDist(map, x, y, 'Z') + (noise2(x * 0.15, y * 0.15, 301) - 0.5) * 2.5;
  if (d < 1.6) return hex('#f4fbfb');
  let c = mix(hex('#3fb8c8'), hex('#14508a'), smoothstep(2, 34, d));
  const wave = Math.sin(d * 0.7 - fbm(x / 30, y / 30, 302) * 4);
  if (d < 16 && wave > 0.93) c = mix(c, hex('#ffffff'), 0.55);
  c = mix(c, hex('#a8dcef'), smoothstep(0.62, 0.78, fbm(x / 40, y / 16, 303)) * 0.25);
  return c;
}

export function colorAt2(map: MapDef, x: number, y: number, mat: string): RGB | null {
  switch (mat) {
    case 'z':
      return terrazzo(x, y);
    case 'n':
      return marketStreet(map, x, y);
    case 'X':
      return granite(x, y);
    case 'Q':
      return brickWalk(x, y);
    case 'm':
      return stationFloor(x, y);
    case 'e':
      return tactile(x, y);
    case 'T':
      return rails(x, y);
    case 'u':
      return marbleFloor(x, y);
    case 'M':
      return cliff(x, y);
    case 'q':
      return river(map, x, y);
    case 'y':
      return river(map, x, y);
    case 'o':
      return pebbles(x, y);
    case 'Z':
      return sea(map, x, y);
    case 'J':
    case 'O':
      return hex('#5a5f66');
    default:
      return colorAt3(map, x, y, mat);
  }
}

export const PLACEHOLDER2: Record<string, string> = {
  z: '#d6cfbf', n: '#625f5c', X: '#c8c2b8', Q: '#b45a42', m: '#e2e6e8', e: '#f2c230', T: '#77726c', u: '#76604e',
  M: '#b0ada4', q: '#4cc0c0', y: '#4cc0c0', o: '#9a968e', Z: '#2a7ab0', J: '#5a7a96', O: '#8a8a88',
};
